import React, { useState } from 'react';
import {
    Typography,
    Box,
    Chip,
    Paper,
    Button,
    Collapse,
    Link,
    Grid
} from '@mui/material';
import {
    Language as Globe,
    OpenInNew as ExternalLink
} from '@mui/icons-material';
import SourceCitation from '../../SourceCitation';
import WebsiteAnalysis from '../../WebsiteAnalysis';
import MainWebsiteCard from './MainWebsiteCard';
import _ from 'lodash';
import { colors } from '../../styles/globals.js';
import { formatExternalUrl } from '../../utils/formatUrl';
import { AddWebsiteButton, RemoveWebPresenceButton, WebsiteWhoisEditor } from '../EditMode/CompanyDetailEditControls';
import WebPresenceAssessmentEditor from '../EditMode/WebPresenceAssessmentEditor';

const getAssessmentKeyColor = (assessment) => {
    if (!assessment || !assessment.name || !assessment.score) {
        return { bg: '#F3F4F6', text: '#6B7280' };
    }

    const score = parseFloat(assessment.score);
    if (score < 1.0) {
        return { bg: '#D1FAE5', text: '#059669' }; // Green
    } else if (score === 1.0) {
        return { bg: '#F3F4F6', text: '#6B7280' }; // Grey
    } else {
        return { bg: '#FEE2E2', text: '#DC2626' }; // Red
    }
};

const AssessmentChips = ({ assessments }) => {
    if (!assessments?.length) return null;
    const filteredAssessments = _.filter(assessments, assessment =>
        assessment.ui_string && assessment.ui_string !== "None"
    );

    if (!filteredAssessments.length) return null;

    return (
        <Box sx={{ pt: 1, p: 2, pt: 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {_.sortBy(
                _.uniqBy(
                    _.filter(filteredAssessments, assessment =>
                        !assessment.ui_string.toLowerCase().includes('could not assess')
                    ),
                    'ui_string'
                ),
                [
                    assessment => {
                        const score = parseFloat(assessment.score);
                        if (score > 1.0) return 0;
                        if (score < 1.0) return 1;
                        return 2;
                    },
                    'score'
                ]
            ).map((assessment, index) => {
                const colors = getAssessmentKeyColor(assessment);
                return (
                    <Chip
                        key={index}
                        label={assessment.ui_string}
                        size="small"
                        sx={{
                            bgcolor: colors.bg,
                            color: colors.text,
                            '& .MuiChip-label': {
                                fontSize: '0.75rem',
                                fontWeight: 500
                            }
                        }}
                    />
                );
            })}
        </Box>
    );
};

const MainWebsiteItem = ({ item }) => {
    const [expanded, setExpanded] = useState(false);
    const colors = { color: '#2196f3', bg: '#e3f2fd' };

    const handleToggle = () => {
        setExpanded(!expanded);
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                mb: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                },
                borderLeft: `3px solid ${colors.color}`,
            }}
            onClick={handleToggle}
        >
            <Box sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: 2
            }}>
                <Box sx={{ flex: '0 1 70%', minWidth: 0 }}>
                    <Box sx={{ minHeight: '24px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            COMPANY WEBSITE
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                        <Link
                            href={formatExternalUrl(item.value)}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {item.value}
                        </Link>
                    </Typography>
                </Box>
                <Box sx={{ flex: '0 1 35%', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <SourceCitation sources={item.source} value={item.value} />
                    <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ExternalLink />}
                        sx={{
                            textTransform: 'none',
                            color: colors.color,
                            borderColor: colors.color,
                            ml: 1,
                            whiteSpace: 'nowrap',
                            '&:hover': {
                                borderColor: colors.color,
                                backgroundColor: colors.bg
                            }
                        }}
                        onClick={e => {
                            e.stopPropagation();
                            window.open(formatExternalUrl(item.value), '_blank');
                        }}
                    >
                        VISIT
                    </Button>
                </Box>
            </Box>
            <Collapse in={expanded} timeout={300} easing="ease-in-out">
                <Box sx={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                }}>
                    <AssessmentChips assessments={item.assessments} />
                    {item.type === 'COMPANY_WEBSITE' && <WebsiteAnalysis data={item.data} />}
                </Box>
            </Collapse>
        </Paper>
    );
};

const MainWebsiteTab = ({ web_presence, onUpdateWebPresence }) => {
    const domains = web_presence.filter(item => item.type === 'COMPANY_WEBSITE' || item.type === 'DOMAIN');

    if (domains.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: colors.textSecondary }}>
                    No official website found
                </Typography>
                {onUpdateWebPresence && (
                    <Box sx={{ mt: 2 }}>
                        <AddWebsiteButton webPresence={web_presence} onUpdate={onUpdateWebPresence} />
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box>
            {onUpdateWebPresence && (
                <Box sx={{ mb: 2 }}>
                    <AddWebsiteButton webPresence={web_presence} onUpdate={onUpdateWebPresence} />
                </Box>
            )}
            <Grid container spacing={2}>
                {domains.map(domain => {
                    // Convert assessments to status items
                    const statusItems = _.map(domain.assessments, assessment => {
                        let type;
                        switch(assessment.result_sign) {
                            case 'Negative':
                                type = 'success';
                                break;
                            case 'Positive':
                                type = 'warning';
                                break;
                            default:
                                type = 'neutral';
                        }

                        return {
                            type,
                            text: assessment.ui_string
                        };
                    }) || [];


                    return (
                        <Grid item xs={6} sm={6} md={6} key={domain.id}>
                            <MainWebsiteCard
                                riskLevel={null}
                                domainTypes={domain.association_type}
                                url={domain.value}
                                statusItems={statusItems}
                                source={domain.source}
                                whoisData={domain.data}
                            />
                            {onUpdateWebPresence && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                                    <RemoveWebPresenceButton
                                        itemId={domain.id}
                                        webPresence={web_presence}
                                        onUpdate={onUpdateWebPresence}
                                    />
                                    <WebPresenceAssessmentEditor
                                        item={domain}
                                        webPresence={web_presence}
                                        onUpdate={onUpdateWebPresence}
                                    />
                                    <WebsiteWhoisEditor
                                        item={domain}
                                        webPresence={web_presence}
                                        onUpdate={onUpdateWebPresence}
                                    />
                                </Box>
                            )}
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default MainWebsiteTab;